import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck, Download, Loader2, Save, RotateCcw, CheckCircle2, AlertCircle,
  User, Mail, Calendar, Search, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ConfidentialityAdmin = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clause, setClause] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [agreements, setAgreements] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const cRes = await api.get('/confidentiality/clause');
      setClause(cRes.data);
      setTitle(cRes.data.title || '');
      setContent(cRes.data.content || '');

      if (isAdmin) {
        const aRes = await api.get('/confidentiality/agreements');
        setAgreements(aRes.data.agreements || []);
        setPendingUsers(aRes.data.pending_users || []);
        setCurrentVersion(aRes.data.current_version || 1);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error('Le contenu de la clause est obligatoire');
      return;
    }
    const contentChanged = content !== clause?.content;
    if (contentChanged) {
      const ok = window.confirm(
        "Modifier le contenu cree une NOUVELLE VERSION. Tous les collaborateurs devront signer a nouveau lors de leur prochaine connexion. Continuer ?"
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const res = await api.put('/confidentiality/clause', { title, content });
      setClause(res.data);
      toast.success(contentChanged ? `Clause mise a jour (v${res.data.version})` : 'Titre mis a jour');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reinitialiser la clause au modele par defaut ? Une nouvelle version sera creee.')) return;
    setSaving(true);
    try {
      const res = await api.post('/confidentiality/clause/reset');
      setClause(res.data);
      setContent(res.data.content);
      setTitle(res.data.title);
      toast.success('Clause reinitialisee au modele par defaut');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttestation = async (agreementId, userName) => {
    try {
      const res = await api.get(`/confidentiality/agreements/${agreementId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_confidentialite_${(userName || 'user').replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Attestation telechargee');
    } catch {
      toast.error('Erreur de telechargement');
    }
  };

  const filteredAgreements = agreements.filter(a => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return a.user_name?.toLowerCase().includes(s) || a.user_email?.toLowerCase().includes(s);
  });

  const filteredPending = pendingUsers.filter(u => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  if (!isAdmin) {
    return (
      <div className="text-center py-20">
        <ShieldCheck className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500">Reserve aux administrateurs</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#1B3A4B]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="confidentiality-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-[#1B3A4B]" />
            Clause de Confidentialite
          </h1>
          <p className="text-gray-500 mt-1">
            Redigez la clause et suivez les signatures de vos collaborateurs
          </p>
        </div>
        <Badge className="bg-[#1B3A4B] text-white text-sm px-3 py-1.5 w-fit">
          Version active : v{currentVersion}
        </Badge>
      </div>

      <Tabs defaultValue="editor" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-3">
          <TabsTrigger value="editor" data-testid="tab-editor">Redaction</TabsTrigger>
          <TabsTrigger value="signed" data-testid="tab-signed">
            Signees ({agreements.filter(a => a.is_current_version).length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            En attente ({pendingUsers.length})
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: EDITOR */}
        <TabsContent value="editor" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" /> Contenu de la clause
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre du document</Label>
                <Input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Clause de Confidentialite"
                  data-testid="clause-title-input"
                />
              </div>
              <div>
                <Label>Contenu</Label>
                <Textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={20}
                  className="font-mono text-sm leading-relaxed"
                  data-testid="clause-content-input"
                  placeholder="Saisissez le texte integral de votre clause..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Astuce : utilisez <code className="bg-gray-100 px-1">{'{{user_name}}'}</code> pour inserer
                  automatiquement le nom du collaborateur. Chaque modification du contenu cree une nouvelle version
                  et oblige les collaborateurs a resigner.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-end pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={saving}
                  data-testid="reset-clause-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Reinitialiser au modele
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                  data-testid="save-clause-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB 2: SIGNED */}
        <TabsContent value="signed" className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par nom ou e-mail..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-signed"
            />
          </div>

          {filteredAgreements.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <ShieldCheck className="w-14 h-14 mx-auto mb-3 opacity-40" />
              <p>Aucune signature enregistree</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAgreements.map(a => (
                <Card key={a.id} data-testid={`agreement-${a.id}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          a.is_current_version ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {a.is_current_version ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{a.user_name}</span>
                            <Badge className={a.is_current_version ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              v{a.clause_version} {a.is_current_version ? '' : '(obsolete)'}
                            </Badge>
                            <Badge variant="outline" className="text-xs">{a.user_role}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{a.user_email}</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />
                              {new Date(a.signed_at).toLocaleString('fr-FR')}
                            </span>
                            {a.employee_id && <span className="font-mono">{a.employee_id}</span>}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadAttestation(a.id, a.user_name)}
                        data-testid={`download-attestation-${a.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" /> Attestation PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TAB 3: PENDING */}
        <TabsContent value="pending" className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-pending"
            />
          </div>

          {filteredPending.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-3 text-green-400" />
              <p className="text-green-600 font-medium">Tous les collaborateurs ont signe la version actuelle</p>
            </div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                Ces collaborateurs devront signer la clause lors de leur prochaine connexion.
              </div>
              <div className="space-y-2">
                {filteredPending.map(u => (
                  <Card key={u.id} data-testid={`pending-${u.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-gray-900">{u.name}</span>
                            <Badge variant="outline" className="text-xs">{u.role}</Badge>
                            {u.employee_id && <Badge variant="outline" className="text-xs font-mono">{u.employee_id}</Badge>}
                          </div>
                          <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" />{u.email}
                          </span>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfidentialityAdmin;
